import { useEffect, useMemo, useState, useCallback, useRef } from "react";

import { 
  getAdminWallets,
  getAdminWalletStats,
  getAdminWalletDetail,
  refreshWalletBalances,
  adjustNairaBalance,
  adjustCryptoBalance,
  regenerateUserWallet,
  clearStuckLock,
  getAdminUserWalletTxns,
  getAssets 
} from "../../../api/apiServices";

// ── Formatters ─────────────────────────────────────────────────
const fmtNGN = (n) =>
  `₦${Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCrypto = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 6 });
const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const EXPLORERS = {
  bsc:    (a) => `https://bscscan.com/address/${a}`,
  eth:    (a) => `https://etherscan.io/address/${a}`,
  ton:    (a) => `https://tonviewer.com/${a}`,
  sol:    (a) => `https://solscan.io/account/${a}`,
  solana: (a) => `https://solscan.io/account/${a}`,
};
const explorerUrl = (net, addr) =>
  (EXPLORERS[net?.toLowerCase()] || ((a) => `https://google.com/search?q=${a}`))(addr);

// ── Confirm Modal ───────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel = "Confirm", confirmColor = "green", onConfirm, onCancel, loading }) {
  const btnCls = {
    green: "bg-[#00C896] hover:bg-[#00b386] text-black",
    red:   "bg-red-600 hover:bg-red-700 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-black",
  }[confirmColor] || "bg-[#00C896] text-black";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-6 pb-4 text-center space-y-2">
          <p className="text-white font-bold">{title}</p>
          <p className="text-white/40 text-sm leading-relaxed">{message}</p>
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button onClick={onCancel} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${btnCls}`}>
            {loading
              ? <><div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />Working…</>
              : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Wallet Detail Modal — 3 tabs: Overview / Transactions / Actions
// ──────────────────────────────────────────────────────────────
function WalletDetailModal({ userId, priceMap, onClose }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState("overview");

  // Transactions
  const [txns, setTxns]               = useState([]);
  const [txPage, setTxPage]           = useState(1);
  const [txTotal, setTxTotal]         = useState(0);
  const [txLoading, setTxLoading]     = useState(false);

  // Action feedback
  const [actionLoading, setActionLoading] = useState(null); // string key | null
  const [actionMsg, setActionMsg]         = useState(null); // { type, text }
  const [confirm, setConfirm]             = useState(null);
  const [copied, setCopied]               = useState("");

  // Naira form
  const [nairaAmount, setNairaAmount] = useState("");
  const [nairaType, setNairaType]     = useState("credit");
  const [nairaNote, setNairaNote]     = useState("");

  // Crypto form
  const [cryptoNet, setCryptoNet]     = useState("");
  const [cryptoCur, setCryptoCur]     = useState("");
  const [cryptoBal, setCryptoBal]     = useState("");
  const [cryptoNote, setCryptoNote]   = useState("");

  // ── Load wallet detail ───────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminWalletDetail(userId);
      if (res.status) setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [userId]);

  // ── Load transactions ────────────────────────────────────────
  const loadTxns = useCallback(async (page = 1) => {
    setTxLoading(true);
    try {
      const res = await getAdminUserWalletTxns(userId, { page, limit: 15 });
      if (res.status) {
        setTxns(prev => page === 1 ? res.data : [...prev, ...res.data]);
        setTxTotal(res.pagination.total);
        setTxPage(page);
      }
    } catch { /* silent */ }
    finally { setTxLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (activeTab === "transactions" && txns.length === 0) loadTxns(1);
  }, [activeTab]);

  // ── Generic action runner ────────────────────────────────────
  const doAction = async (key, fn, successMsg) => {
    setActionLoading(key);
    setActionMsg(null);
    try {
      const res = await fn();
      setActionMsg({ type: "success", text: res.message || successMsg });
      await load();
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.message || "Action failed" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  };

  const handleRefresh    = () => doAction("refresh", () => refreshWalletBalances(userId), "Balances refreshed");
  const handleRegenerate = () => doAction("regen",   () => regenerateUserWallet(userId),  "Wallet regenerated");
  const handleClearLock  = (net) => doAction(`lock_${net}`, () => clearStuckLock(userId, net), "Lock cleared");

  const handleNairaAdjust = () =>
    doAction("naira", () => adjustNairaBalance(userId, Number(nairaAmount), nairaType, nairaNote),
      `₦${Number(nairaAmount).toLocaleString()} ${nairaType}ed`);

  const handleCryptoAdjust = () =>
    doAction("crypto", () => adjustCryptoBalance(userId, cryptoNet, cryptoCur, Number(cryptoBal), cryptoNote),
      "Balance updated");

  // ── Derived ──────────────────────────────────────────────────
  const wallet         = data;
  const user           = data?.user;
  const networkEntries = wallet?.networks || [];
  const stuckNetworks  = networkEntries.filter(n => n.isProcessingDeposit);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-2xl bg-[#161616] border border-white/8 rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 flex-shrink-0">
          {loading ? (
            <div className="h-4 w-40 bg-white/10 rounded animate-pulse" />
          ) : (
            <div>
              <p className="text-white font-bold text-sm">{user?.userName || user?.email || userId}</p>
              <p className="text-white/40 text-xs mt-0.5">{user?.email}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {user && (
              <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full capitalize ${
                user.kycStatus === "approved"
                  ? "bg-[#00C896]/15 text-[#00C896]"
                  : "bg-white/5 text-white/40"
              }`}>{user.kycStatus || "no kyc"}</span>
            )}
            <button onClick={onClose}
              className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
              <i className="ri-close-line text-white/60" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/5 flex-shrink-0">
          {[
            { id: "overview",     label: "Overview",     icon: "ri-wallet-3-line" },
            { id: "transactions", label: "Transactions", icon: "ri-exchange-line" },
            { id: "actions",      label: "Actions",      icon: "ri-tools-line" },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 text-xs font-semibold transition-all border-b-2 ${
                activeTab === tab.id
                  ? "text-[#00C896] border-[#00C896]"
                  : "text-white/30 border-transparent hover:text-white/60"
              }`}>
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-12 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : !wallet ? (
            <div className="flex items-center justify-center p-10 text-white/30 text-sm">
              Wallet not found
            </div>
          ) : (

            // ── OVERVIEW ────────────────────────────────────────
            activeTab === "overview" ? (
              <div className="p-5 space-y-5">

                {/* Naira + Referral */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Naira Balance",    value: fmtNGN(wallet.nairaBalance),    color: "#00C896", icon: "ri-money-dollar-circle-line" },
                    { label: "Referral Balance", value: fmtNGN(wallet.referralBalance), color: "#7C3AED", icon: "ri-gift-line" },
                  ].map(s => (
                    <div key={s.label}
                      className="bg-white/4 rounded-xl p-4 border border-white/5 text-center">
                      <i className={`${s.icon} text-xl mb-2 block`} style={{ color: s.color }} />
                      <p className="text-white font-bold text-base">{s.value}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Stuck lock warning */}
                {stuckNetworks.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 space-y-2">
                    <p className="text-amber-400 text-xs font-bold flex items-center gap-1.5">
                      <i className="ri-alert-line" /> Stuck Processing Lock Detected
                    </p>
                    {stuckNetworks.map(n => (
                      <div key={n.network} className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">
                          {n.network.toUpperCase()} — stuck since {fmtDate(n.processingStartedAt)}
                        </span>
                        <button
                          onClick={() => setConfirm({
                            title:     "Clear Stuck Lock",
                            message:   `Clear the processing lock on ${n.network.toUpperCase()}?`,
                            color:     "amber",
                            onConfirm: () => handleClearLock(n.network),
                          })}
                          className="text-[10px] font-bold px-3 py-1 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors">
                          Clear Lock
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Assets grouped by currency */}
                <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                  <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5 font-bold">
                    Assets
                  </p>
                  {!(wallet.assets?.length) ? (
                    <p className="text-white/20 text-xs text-center py-8">No assets</p>
                  ) : wallet.assets.map(asset => {
                    const price    = priceMap[asset.currency] || 0;
                    const nairaVal = asset.totalBalance * price;
                    return (
                      <div key={asset.currency} className="border-b border-white/4 last:border-0">
                        {/* Currency header */}
                        <div className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#00C896]/10 flex items-center justify-center text-[#00C896] text-[10px] font-bold">
                              {asset.currency.slice(0, 2)}
                            </div>
                            <span className="text-white font-semibold text-sm">{asset.currency}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-bold text-sm">{fmtCrypto(asset.totalBalance)}</p>
                            {nairaVal > 0 && (
                              <p className="text-[#00C896] text-[10px]">{fmtNGN(nairaVal)}</p>
                            )}
                          </div>
                        </div>

                        {/* Per-network rows */}
                        {asset.networks.map(netEntry => (
                          <div key={netEntry.network}
                            className="flex items-center gap-3 px-4 py-2.5 bg-white/2 border-t border-white/4">
                            <span className="text-[10px] font-bold text-white/30 uppercase w-8 flex-shrink-0">
                              {netEntry.network}
                            </span>
                            <span className="text-[10px] font-mono text-white/40 flex-1 truncate">
                              {netEntry.address}
                            </span>
                            <span className="text-xs text-white/60 flex-shrink-0">
                              {fmtCrypto(netEntry.balance)}
                            </span>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleCopy(netEntry.address, `${netEntry.network}_${netEntry.address}`)}
                                className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors"
                                title="Copy address">
                                <i className={`text-[10px] ${
                                  copied === `${netEntry.network}_${netEntry.address}`
                                    ? "ri-check-line text-[#00C896]"
                                    : "ri-file-copy-line text-white/40"
                                }`} />
                              </button>
                              <a href={explorerUrl(netEntry.network, netEntry.address)}
                                target="_blank" rel="noreferrer"
                                className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors"
                                title="View on explorer">
                                <i className="ri-external-link-line text-[10px] text-white/40" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )

            // ── TRANSACTIONS ─────────────────────────────────────
            : activeTab === "transactions" ? (
              <div className="p-4 space-y-3">
                <p className="text-white/30 text-xs px-1">{txTotal} total transactions</p>
                {txLoading && txns.length === 0 ? (
                  [...Array(5)].map((_, i) => (
                    <div key={i} className="h-14 bg-white/5 rounded-xl animate-pulse" />
                  ))
                ) : txns.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <i className="ri-exchange-line text-white/15 text-4xl mb-2" />
                    <p className="text-white/25 text-sm">No transactions</p>
                  </div>
                ) : (
                  <>
                    {txns.map(tx => (
                      <div key={tx._id}
                        className="flex items-center gap-3 bg-white/4 rounded-xl p-3 border border-white/5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          tx.type === "deposit"           ? "bg-[#00C896]/10"
                          : tx.type === "admin_adjustment" ? "bg-white/5"
                          : "bg-amber-500/10"
                        }`}>
                          <i className={`text-sm ${
                            tx.type === "deposit"           ? "ri-arrow-down-circle-line text-[#00C896]"
                            : tx.type === "admin_adjustment" ? "ri-settings-3-line text-white/40"
                            : "ri-arrow-up-circle-line text-amber-400"
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-medium capitalize">{tx.type.replace(/_/g, " ")}</p>
                          <p className="text-white/30 text-[10px]">{tx.asset} · {fmtDate(tx.createdAt)}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-white text-xs font-bold">{fmtNGN(tx.nairaAmount)}</p>
                          <span className={`text-[10px] font-semibold ${
                            tx.status === "completed" ? "text-[#00C896]"
                            : tx.status === "pending"  ? "text-amber-400"
                            : "text-red-400"
                          }`}>{tx.status}</span>
                        </div>
                      </div>
                    ))}
                    {txns.length < txTotal && (
                      <button onClick={() => loadTxns(txPage + 1)} disabled={txLoading}
                        className="w-full py-3 text-xs font-semibold text-[#00C896] hover:text-[#00b386] transition-colors disabled:opacity-50 text-center">
                        {txLoading ? "Loading…" : `Load more (${txTotal - txns.length} remaining)`}
                      </button>
                    )}
                  </>
                )}
              </div>
            )

            // ── ACTIONS ───────────────────────────────────────────
            : (
              <div className="p-5 space-y-4">
                {/* Feedback */}
                {actionMsg && (
                  <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium border ${
                    actionMsg.type === "success"
                      ? "bg-[#00C896]/10 border-[#00C896]/20 text-[#00C896]"
                      : "bg-red-500/10 border-red-500/20 text-red-400"
                  }`}>
                    <i className={actionMsg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                    {actionMsg.text}
                    <button onClick={() => setActionMsg(null)} className="ml-auto opacity-60 hover:opacity-100">
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                )}

                {/* Quick actions */}
                <div className="bg-white/4 rounded-xl border border-white/5 p-4 space-y-3">
                  <p className="text-white text-sm font-semibold">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      disabled={!!actionLoading}
                      onClick={() => setConfirm({
                        title:     "Refresh Balances",
                        message:   "Re-fetch all on-chain balances for this user? This does not trigger a sweep.",
                        color:     "green",
                        onConfirm: handleRefresh,
                      })}
                      className="py-2.5 rounded-xl bg-[#00C896]/10 text-[#00C896] text-xs font-bold border border-[#00C896]/20 hover:bg-[#00C896]/20 transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5">
                      <i className={`ri-refresh-line ${actionLoading === "refresh" ? "animate-spin" : ""}`} />
                      {actionLoading === "refresh" ? "Refreshing…" : "Refresh Balances"}
                    </button>
                    <button
                      disabled={!!actionLoading}
                      onClick={() => setConfirm({
                        title:     "Regenerate Wallet",
                        message:   "Sync wallet with the latest active currencies? No existing balances will be lost.",
                        color:     "green",
                        onConfirm: handleRegenerate,
                      })}
                      className="py-2.5 rounded-xl bg-white/5 text-white/60 text-xs font-bold border border-white/8 hover:bg-white/10 transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5">
                      <i className={`ri-refresh-2-line ${actionLoading === "regen" ? "animate-spin" : ""}`} />
                      {actionLoading === "regen" ? "Regenerating…" : "Regenerate Wallet"}
                    </button>
                  </div>
                </div>

                {/* Adjust Naira */}
                <div className="bg-white/4 rounded-xl border border-white/5 p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white text-sm font-semibold">Naira Balance Adjustment</p>
                      <p className="text-white/30 text-xs mt-0.5">Current: {fmtNGN(wallet.nairaBalance)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {["credit", "debit"].map(t => (
                      <button key={t} onClick={() => setNairaType(t)}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold capitalize transition-all border ${
                          nairaType === t
                            ? t === "credit"
                              ? "bg-[#00C896]/20 text-[#00C896] border-[#00C896]/30"
                              : "bg-red-500/20 text-red-400 border-red-500/30"
                            : "bg-white/5 text-white/30 border-white/8"
                        }`}>
                        {t === "credit" ? "+ Credit" : "− Debit"}
                      </button>
                    ))}
                  </div>
                  <input type="number" value={nairaAmount} onChange={e => setNairaAmount(e.target.value)}
                    placeholder="Amount (₦)"
                    className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                  <input value={nairaNote} onChange={e => setNairaNote(e.target.value)}
                    placeholder="Reason / internal note (optional)"
                    className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                  <button
                    disabled={!nairaAmount || !!actionLoading}
                    onClick={() => setConfirm({
                      title:     `Confirm ${nairaType === "credit" ? "Credit" : "Debit"}`,
                      message:   `${nairaType === "credit" ? "Credit" : "Debit"} ₦${Number(nairaAmount).toLocaleString()} ${nairaType === "credit" ? "to" : "from"} this user's naira balance?`,
                      color:     nairaType === "credit" ? "green" : "red",
                      onConfirm: handleNairaAdjust,
                    })}
                    className="w-full py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-40">
                    {actionLoading === "naira" ? "Processing…" : `Confirm ${nairaType === "credit" ? "Credit" : "Debit"}`}
                  </button>
                </div>

                {/* Adjust Crypto */}
                <div className="bg-white/4 rounded-xl border border-white/5 p-4 space-y-3">
                  <div>
                    <p className="text-white text-sm font-semibold">Crypto Balance Override</p>
                    <p className="text-white/30 text-xs mt-0.5">Manually set the stored balance for a specific token</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Network</label>
                      <select value={cryptoNet} onChange={e => { setCryptoNet(e.target.value); setCryptoCur(""); }}
                        className="w-full bg-[#161616] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C896]/40">
                        <option value="">Select network</option>
                        {networkEntries.map(n => (
                          <option key={n.network} value={n.network}>{n.network.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Token</label>
                      <select value={cryptoCur} onChange={e => setCryptoCur(e.target.value)}
                        className="w-full bg-[#161616] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C896]/40">
                        <option value="">Select token</option>
                        {(networkEntries.find(n => n.network === cryptoNet)?.tokens || []).map(t => (
                          <option key={t.currency} value={t.currency}>
                            {t.currency} (current: {fmtCrypto(t.balance)})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <input type="number" value={cryptoBal} onChange={e => setCryptoBal(e.target.value)}
                    placeholder="New balance"
                    className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                  <input value={cryptoNote} onChange={e => setCryptoNote(e.target.value)}
                    placeholder="Reason / internal note (optional)"
                    className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                  <button
                    disabled={!cryptoNet || !cryptoCur || cryptoBal === "" || !!actionLoading}
                    onClick={() => setConfirm({
                      title:     "Override Crypto Balance",
                      message:   `Set ${cryptoCur} balance on ${cryptoNet?.toUpperCase()} to ${cryptoBal}? This only changes the stored record — it does NOT move on-chain funds.`,
                      color:     "amber",
                      onConfirm: handleCryptoAdjust,
                    })}
                    className="w-full py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-40">
                    {actionLoading === "crypto" ? "Updating…" : "Set Balance"}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Confirm prompt */}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel="Confirm"
          confirmColor={confirm.color || "green"}
          loading={!!actionLoading}
          onConfirm={async () => { await confirm.onConfirm(); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Main WalletsView
// ──────────────────────────────────────────────────────────────
export default function WalletsView() {
  const [wallets, setWallets]       = useState([]);
  const [stats, setStats]           = useState(null);
  const [assets, setAssets]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch]         = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const searchTimer                 = useRef(null);

  // Build price map { "USDT": 1350, "BNB": 950000, ... }
  const priceMap = useMemo(() =>
    assets.reduce((acc, a) => {
      acc[a.symbol] = a.priceNgn || 0;
      return acc;
    }, {}),
    [assets]
  );

  // ── Fetch ──────────────────────────────────────────────────
  const fetchAll = useCallback(async (p = 1, q = search) => {
    if (p === 1) setLoading(true);
    try {
      const promises = [getAdminWallets({ page: p, limit: 20, search: q })];
      if (p === 1) {
        promises.push(getAdminWalletStats());
        promises.push(getAssets());
      }

      const [walletsRes, statsRes, assetsRes] = await Promise.all(promises);

      if (walletsRes?.status) {
        setWallets(prev => p === 1 ? walletsRes.data : [...prev, ...walletsRes.data]);
        setTotalPages(walletsRes.pagination.pages);
        setPage(p);
      }
      if (statsRes?.status)  setStats(statsRes.data);
      if (assetsRes)         setAssets(Array.isArray(assetsRes) ? assetsRes : (assetsRes.data || []));
    } catch (err) {
      console.error("[WalletsView] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchAll(1); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchAll(1, val), 400);
  };

  // ── Flatten all wallets into table rows ────────────────────
  // One row per (user, currency, network)
  const rows = useMemo(() => {
    const out = [];
    wallets.forEach(u => {
      (u.assets || []).forEach(asset => {
        asset.networks.forEach(net => {
          const price    = priceMap[asset.currency] || 0;
          const nairaVal = net.balance * price;
          // Stuck-lock flag from the network entry stored on the user object
          const isStuck  = (u.networks || [])
            .find(n => n.network === net.network)?.isProcessingDeposit || false;
          out.push({
            userId:    u.userId,
            userEmail: u.user?.email    || u.userId,
            userName:  u.user?.userName || "",
            kycStatus: u.user?.kycStatus || "",
            currency:  asset.currency,
            network:   net.network,
            address:   net.address,
            balance:   net.balance,
            nairaVal,
            isStuck,
          });
        });
      });
    });
    return out;
  }, [wallets, priceMap]);

  const totalNairaValue = useMemo(() =>
    rows.reduce((s, r) => s + r.nairaVal, 0), [rows]
  );

  const uniqueNetworks = useMemo(() => new Set(rows.map(r => r.network)).size, [rows]);

  return (
    <div className="space-y-5">

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Hot Wallet Value", value: fmtNGN(totalNairaValue),             icon: "ri-safe-line",                  color: "#00C896" },
          { label: "Total Users",            value: String(stats?.totalWallets || 0),    icon: "ri-wallet-3-line",              color: "#7C3AED" },
          { label: "Naira Held",             value: fmtNGN(stats?.totalNairaHeld || 0),  icon: "ri-money-dollar-circle-line",   color: "#D97706" },
          { label: "Networks",               value: String(uniqueNetworks),              icon: "ri-global-line",                color: "#0284C7" },
        ].map(s => (
          <div key={s.label} className="bg-[#161616] border border-white/5 rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
              style={{ background: s.color + "18" }}>
              <i className={`${s.icon} text-base`} style={{ color: s.color }} />
            </div>
            <div>
              <p className="text-lg font-bold text-white leading-none">{s.value}</p>
              <p className="text-[10px] text-white/40 mt-1 leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Search + refresh ── */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by email or username…"
            className="w-full bg-[#161616] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40"
          />
        </div>
        <button onClick={() => fetchAll(1)}
          className="px-4 rounded-xl bg-[#161616] border border-white/8 text-white/50 hover:text-white hover:border-[#00C896]/30 transition-colors">
          <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* ── Desktop table ── */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden hidden md:block">
        <div className="px-5 py-3 border-b border-white/5">
          <span className="text-xs text-white/40">{rows.length} entries across {wallets.length} users</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                {["User", "Asset", "Network", "Address", "Balance", "NGN Value", "Status", ""].map(h => (
                  <th key={h}
                    className="text-left text-[10px] font-bold text-white/25 uppercase tracking-wider px-5 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-white/4">
                    {[...Array(8)].map((_, j) => (
                      <td key={j} className="px-5 py-4">
                        <div className="h-3 bg-white/5 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center">
                    <i className="ri-wallet-3-line text-white/10 text-4xl block mb-2" />
                    <p className="text-white/25 text-sm">No wallets found</p>
                  </td>
                </tr>
              ) : rows.map((row, i) => (
                <tr key={i}
                  className="border-b border-white/4 last:border-0 hover:bg-white/2 transition-colors cursor-pointer"
                  onClick={() => setSelectedUserId(row.userId)}>
                  <td className="px-5 py-3.5">
                    <p className="text-white text-xs font-medium truncate max-w-[130px]">
                      {row.userName || row.userEmail}
                    </p>
                    {row.userName && (
                      <p className="text-white/30 text-[10px] truncate max-w-[130px]">{row.userEmail}</p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#00C896]/10 flex items-center justify-center text-[#00C896] text-[9px] font-bold">
                        {row.currency.slice(0, 2)}
                      </div>
                      <span className="text-xs font-bold text-white">{row.currency}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/50 uppercase">
                      {row.network}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[10px] font-mono text-white/35 max-w-[130px] truncate block">
                      {row.address}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs font-semibold text-white">{fmtCrypto(row.balance)}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs font-semibold text-[#00C896]">{fmtNGN(row.nairaVal)}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${row.isStuck ? "bg-amber-500 animate-pulse" : "bg-[#00C896]"}`} />
                      <span className={`text-xs ${row.isStuck ? "text-amber-500" : "text-[#00C896]"}`}>
                        {row.isStuck ? "Processing" : "Active"}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedUserId(row.userId); }}
                      className="text-xs text-[#00C896] hover:underline font-semibold">
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {page < totalPages && (
          <div className="px-5 py-3 border-t border-white/4">
            <button onClick={() => fetchAll(page + 1)} disabled={loading}
              className="w-full py-2.5 text-xs font-semibold text-[#00C896] hover:text-[#00b386] transition-colors disabled:opacity-50 text-center">
              {loading ? "Loading…" : `Load more users (${wallets.length} of ${totalPages * 20} loaded)`}
            </button>
          </div>
        )}
      </div>

      {/* ── Mobile cards ── */}
      <div className="md:hidden space-y-3">
        {loading && rows.length === 0 ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-[#161616] rounded-xl border border-white/5 animate-pulse" />
          ))
        ) : rows.map((row, i) => (
          <div key={i}
            className="bg-[#161616] border border-white/5 rounded-xl p-4 cursor-pointer active:scale-[0.99] transition-transform"
            onClick={() => setSelectedUserId(row.userId)}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#00C896]/10 flex items-center justify-center text-[#00C896] text-xs font-bold">
                  {row.currency.slice(0, 2)}
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{row.currency}</p>
                  <p className="text-xs text-white/40">
                    {row.network.toUpperCase()} · {row.userName || row.userEmail}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${row.isStuck ? "bg-amber-500 animate-pulse" : "bg-[#00C896]"}`} />
                <span className={`text-xs ${row.isStuck ? "text-amber-500" : "text-[#00C896]"}`}>
                  {row.isStuck ? "Processing" : "Active"}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-[#0f0f0f] rounded-lg p-2.5">
                <p className="text-[10px] text-white/30 mb-0.5">Balance</p>
                <p className="text-xs font-semibold text-white">{fmtCrypto(row.balance)}</p>
              </div>
              <div className="bg-[#0f0f0f] rounded-lg p-2.5">
                <p className="text-[10px] text-white/30 mb-0.5">NGN Value</p>
                <p className="text-xs font-semibold text-[#00C896]">{fmtNGN(row.nairaVal)}</p>
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); setSelectedUserId(row.userId); }}
              className="w-full py-2 rounded-lg bg-[#00C896]/10 text-[#00C896] text-xs font-bold hover:bg-[#00C896]/20 transition-colors">
              Manage Wallet
            </button>
          </div>
        ))}
      </div>

      {/* ── Detail modal ── */}
      {selectedUserId && (
        <WalletDetailModal
          userId={selectedUserId}
          priceMap={priceMap}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}
